import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import React, { useCallback, useMemo, useState } from 'react';
import { FormikErrors } from 'formik';
import { DatePicker } from '@mui/x-date-pickers';
import { dateToIsoString, formatStringToDate } from '../../../utils';

import _ from 'lodash';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import { COLORS } from '../../../utils/colors';
import { HeadcountChange, ProjectDetail } from '../../../types';
import { HeadcountChangeFormik } from '../EditHeadcountChangesModal';

interface HeadcountChangeRowProps {
  headcountChangeKey: string;
  headcountChanges: HeadcountChange[];
  projects: ProjectDetail[] | undefined;
  errors: FormikErrors<HeadcountChangeFormik>;
  isSingleProjectSelected: boolean;
  handleDataChange: (
    field: string,
    value: number | string | null,
    index: number,
  ) => void;
  handleDeleteHeadcount: (indexToBeDeleted: number, id?: string) => void;
  findIndex: (id: string) => number;
}

const HeadcountChangeRow: React.FC<HeadcountChangeRowProps> = ({
  findIndex,
  headcountChangeKey,
  headcountChanges,
  projects,
  handleDataChange,
  handleDeleteHeadcount,
  isSingleProjectSelected,
  errors,
}) => {
  const projectsMap = useMemo(
    () => new Map(projects?.map((project) => [project.id, project.name])),
    [projects],
  );
  const [open, setOpen] = useState(true);

  const handleSetOpenCard = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      setOpen(!open);
    },
    [setOpen, open],
  );

  return (
    <>
      {!isSingleProjectSelected && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 0,
            backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
            paddingY: 2,
            marginBottom: 2,
            borderRadius: 2,
          }}
        >
          <ListItem sx={{ paddingY: 0, paddingX: 0 }}>
            <IconButton
              aria-label="Expand/collapse icon"
              size="small"
              onClick={handleSetOpenCard}
            >
              {open ? <UnfoldLessRoundedIcon /> : <UnfoldMoreRoundedIcon />}
            </IconButton>
            <Typography whiteSpace="nowrap" variant="h6" fontWeight={'bold'}>
              {headcountChangeKey === 'new'
                ? 'New projected headcounts'
                : projectsMap.get(headcountChangeKey)}
            </Typography>
          </ListItem>
        </Box>
      )}

      {headcountChanges.map(
        (headcountChange) =>
          open && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                marginBottom: 2,
                px: isSingleProjectSelected ? 0 : 2,
              }}
              key={headcountChange.id ?? headcountChange.tempId}
            >
              <DatePicker
                onChange={(date) =>
                  handleDataChange(
                    'date',
                    dateToIsoString(date),
                    findIndex(
                      headcountChange.id ?? headcountChange.tempId ?? '',
                    ),
                  )
                }
                value={formatStringToDate(headcountChange.date)}
                format="MM/dd/yyyy"
                label="Date"
                sx={{ width: '75%' }}
                disablePast
                disableHighlightToday
                shouldDisableDate={(date) =>
                  date.getDate() === new Date().getDate()
                }
                slotProps={{
                  textField: {
                    error: Boolean(
                      errors.headcountChanges?.[
                        findIndex(
                          headcountChange.id ?? headcountChange.tempId ?? '',
                        )
                      ],
                    ),
                    id: 'Date',
                    label: 'Start Date',
                    name: 'date',
                  },
                }}
              />

              <FormControl fullWidth>
                <InputLabel id="fiterbyfieldlabel">Project</InputLabel>
                <Select
                  label="Project"
                  labelId="filterValuelabel"
                  name={headcountChange.id ? 'tempProjectId' : 'projectId'}
                  error={Boolean(
                    errors.headcountChanges?.[
                      findIndex(
                        headcountChange.id ?? headcountChange.tempId ?? '',
                      )
                    ],
                  )}
                  value={
                    headcountChange.tempProjectId || headcountChange.projectId
                  }
                  onChange={(e) =>
                    handleDataChange(
                      e.target.name,
                      e.target.value,
                      findIndex(
                        headcountChange.id ?? headcountChange.tempId ?? '',
                      ),
                    )
                  }
                  disabled={isSingleProjectSelected}
                >
                  {!projects?.length && (
                    <MenuItem disabled>No options available.</MenuItem>
                  )}

                  {projects?.length &&
                    projects.map((project: ProjectDetail) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField
                id="countChange"
                type="number"
                sx={{
                  width: '40%',
                  '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
                label="People"
                placeholder="Add headcount change"
                value={headcountChange.countChange}
                name={`countChange`}
                error={Boolean(
                  errors.headcountChanges?.[
                    findIndex(
                      headcountChange.id ?? headcountChange.tempId ?? '',
                    )
                  ],
                )}
                onChange={(e) =>
                  handleDataChange(
                    e.target.name,
                    e.target.value,
                    findIndex(
                      headcountChange.id ?? headcountChange.tempId ?? '',
                    ),
                  )
                }
              />

              <IconButton
                aria-label="Delete icon"
                onClick={() =>
                  handleDeleteHeadcount(
                    findIndex(
                      headcountChange.id ?? headcountChange.tempId ?? '',
                    ),
                    headcountChange.id,
                  )
                }
                sx={{
                  fontWeight: '400',
                  fontSize: '14px',
                  width: 30,
                  height: 30,
                  alignSelf: 'center',
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Box>
          ),
      )}
    </>
  );
};

export default HeadcountChangeRow;
